import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, employeesAPI, companiesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Building2, 
  Clock, 
  Calendar, 
  DollarSign, 
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ArrowRight
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#F97316', '#84CC16'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, activitiesRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRecentActivities(5)
      ]);
      setStats(statsRes.data);
      setActivities(activitiesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="text-emerald-500" size={16} />;
      case 'rejected':
        return <XCircle className="text-red-500" size={16} />;
      default:
        return <AlertCircle className="text-amber-500" size={16} />;
    }
  };

  const headcountData = stats?.headcount_by_country 
    ? Object.entries(stats.headcount_by_country).map(([country, count]) => ({
        name: country,
        value: count
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Welcome to Pride Global HRMS</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        <Card className="card-hover" data-testid="stat-total-employees">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Employees</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_employees || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="text-primary" size={24} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-emerald-500 font-medium">{stats?.active_employees || 0}</span>
              <span className="text-muted-foreground ml-1">active</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-companies">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Companies</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_companies || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Building2 className="text-emerald-500" size={24} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm text-muted-foreground">
              <span>Across 9 countries</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-attendance">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Attendance</p>
                <p className="text-3xl font-bold mt-1">{stats?.attendance_today?.present || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Clock className="text-amber-500" size={24} />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3 text-xs flex-wrap">
              <span className="text-red-600 font-medium">
                {stats?.attendance_today?.absent || 0} absent
              </span>
              <span className="text-indigo-600 font-medium" data-testid="stat-attendance-od">
                {stats?.attendance_today?.on_od || 0} on OD
              </span>
              <span className="text-orange-600 font-medium" data-testid="stat-attendance-missing">
                {stats?.attendance_today?.missing_punch || 0} missing punch
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-payroll">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Payroll This Month</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(stats?.payroll_this_month || 0)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <DollarSign className="text-blue-500" size={24} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm text-muted-foreground">
              <TrendingUp size={14} className="mr-1 text-emerald-500" />
              <span>Processed</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-hover" data-testid="pending-leaves-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Calendar className="text-amber-500" size={20} />
                </div>
                <div>
                  <p className="font-medium">Pending Leave Requests</p>
                  <p className="text-sm text-muted-foreground">Awaiting approval</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-lg px-3 py-1">
                {stats?.pending_leaves || 0}
              </Badge>
            </div>
            <Link 
              to="/leaves" 
              className="mt-4 flex items-center text-sm text-primary hover:underline"
              data-testid="view-leaves-link"
            >
              View all requests <ArrowRight size={14} className="ml-1" />
            </Link>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="pending-expenses-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <DollarSign className="text-blue-500" size={20} />
                </div>
                <div>
                  <p className="font-medium">Pending Expense Claims</p>
                  <p className="text-sm text-muted-foreground">Awaiting review</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-lg px-3 py-1">
                {stats?.pending_expenses || 0}
              </Badge>
            </div>
            <Link 
              to="/expenses" 
              className="mt-4 flex items-center text-sm text-primary hover:underline"
              data-testid="view-expenses-link"
            >
              View all claims <ArrowRight size={14} className="ml-1" />
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="headcount-chart">
          <CardHeader>
            <CardTitle>Headcount by Country</CardTitle>
          </CardHeader>
          <CardContent>
            {headcountData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={headcountData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {headcountData.map((entry, index) => (
                        <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {headcountData.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="recent-activities">
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
          </CardHeader>
          <CardContent>
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((activity, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
                  >
                    {getStatusIcon(activity.status)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{activity.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant={
                      activity.status === 'approved' ? 'success' :
                      activity.status === 'rejected' ? 'destructive' : 'secondary'
                    }>
                      {activity.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No recent activities
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
